/**
 * Dashboard shell.
 *
 * Layout is exactly the one in the spec:
 *
 *   ┌──────────┬─────────────────────────┬──────────────┐
 *   │ Watchlist│      Main chart         │ Signal panel │
 *   ├──────────┴─────────────────────────┴──────────────┤
 *   │        News feed / Events / Backtest tabs         │
 *   └──────────────────────────────────────────────────┘
 *
 * Selecting a symbol updates every panel over the WebSocket -- no reload.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { Candle, WsMessage } from '@/api/types'
import { BacktestPanel } from '@/components/Backtest/BacktestPanel'
import { ChartPanel } from '@/components/Chart/ChartPanel'
import { EventList } from '@/components/NewsFeed/EventList'
import { SourceHealth } from '@/components/Health/SourceHealth'
import { MarketSignals } from '@/components/MarketSignals/MarketSignals'
import { NewsFeed } from '@/components/NewsFeed/NewsFeed'
import { SignalPanel } from '@/components/SignalPanel/SignalPanel'
import { Watchlist } from '@/components/Watchlist/Watchlist'
import { TIMEFRAME_SECONDS, bucketStart } from '@/theme/chart'
import { useHealth, useSymbols } from '@/hooks/useQueries'
import { useDeepLink } from '@/hooks/useDeepLink'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useAppStore, type BottomTab } from '@/store/useAppStore'

const TABS: { key: BottomTab; label: string }[] = [
  { key: 'news', label: 'Tin tức' },
  { key: 'events', label: 'Lịch vĩ mô' },
  { key: 'backtest', label: 'Backtest' },
  { key: 'market', label: 'Tín hiệu toàn thị trường' },
  { key: 'sources', label: 'Nguồn dữ liệu' },
]

export default function App() {
  const ticker = useAppStore((s) => s.selectedTicker)
  const timeframe = useAppStore((s) => s.timeframe)
  const bottomTab = useAppStore((s) => s.bottomTab)
  const setBottomTab = useAppStore((s) => s.setBottomTab)

  const symbolsQuery = useSymbols()
  const symbolId = useMemo(
    () => symbolsQuery.data?.find((s) => s.ticker === ticker)?.id ?? null,
    [symbolsQuery.data, ticker],
  )
  // Subscribe to everything on the watchlist so its rows tick live too, not
  // just the charted symbol.
  const watchedIds = useMemo(
    () => (symbolsQuery.data ?? []).map((s) => s.id).sort((a, b) => a - b),
    [symbolsQuery.data],
  )

  // symbol_id -> last traded price seen on the socket.
  const [livePrices, setLivePrices] = useState<Record<number, number>>({})

  // The forming candle arrives on bar.tick and is applied straight to the
  // chart. It is display only -- the engine reads its own bars and refuses
  // anything not marked final.
  const [liveCandle, setLiveCandle] = useState<Candle | null>(null)

  // Drop the forming candle whenever the chart changes what it is showing.
  //
  // Bar buckets are aligned to the wall clock, so 15m candles for every
  // symbol share the same `time`. Without this, switching from ETHUSDT to
  // VIC left ETH's candle in state, the first VIC tick matched it on time,
  // and the reducer kept ETH's `open` while taking VIC's `close` -- the
  // header then read "+10023%" off a candle spanning two different
  // instruments. Symbol-filtering the incoming tick is not enough on its
  // own; the stale candle has to go.
  useEffect(() => {
    setLiveCandle(null)
  }, [symbolId, timeframe])

  const handleMessage = useCallback(
    (message: WsMessage) => {
      if (message.type !== 'bar.tick' && message.type !== 'bar.closed') return
      const payload = message.payload as Record<string, unknown>

      const tickSymbolId = Number(payload.symbol_id)
      const tickClose = Number(payload.close)
      if (Number.isFinite(tickSymbolId) && Number.isFinite(tickClose)) {
        setLivePrices((previous) =>
          previous[tickSymbolId] === tickClose
            ? previous
            : { ...previous, [tickSymbolId]: tickClose },
        )
      }

      // Everything below drives the chart, which shows one symbol and one
      // timeframe at a time.
      if (symbolId !== null && payload.symbol_id !== symbolId) return

      // Accept a tick from the displayed timeframe or any finer one, and
      // fold it into the bucket on screen. Binance publishes a kline per
      // subscribed timeframe, but the Vietnamese adapter publishes 1-minute
      // bars only -- filtering on an exact match dropped every VN tick and
      // left those charts frozen while the price beside them moved.
      const tickTimeframe = String(payload.timeframe)
      const tickSize = TIMEFRAME_SECONDS[tickTimeframe]
      const chartSize = TIMEFRAME_SECONDS[timeframe]
      if (!tickSize || !chartSize || tickSize > chartSize) return

      const close = Number(payload.close)
      const barTs = Math.floor(new Date(String(payload.bar_ts)).getTime() / 1000)
      if (!Number.isFinite(close) || !Number.isFinite(barTs)) return
      const time = bucketStart(barTs, timeframe)

      // A finer bar closing says nothing about the displayed bucket: a 1m
      // bar closing does not close the 15m one it sits inside.
      const closesTheBucket =
        message.type === 'bar.closed' && tickSize === chartSize

      // bar.tick carries only a close; extend the previous shape so the
      // candle body stays sane until the next full fetch.
      setLiveCandle((previous) =>
        previous && previous.time === time
          ? {
              ...previous,
              close,
              high: Math.max(previous.high, close),
              low: Math.min(previous.low, close),
              is_final: closesTheBucket,
            }
          : {
              time, open: close, high: close, low: close, close,
              volume: 0, is_final: closesTheBucket,
            },
      )
    },
    [symbolId, timeframe],
  )

  useWebSocket(watchedIds, handleMessage)
  useDeepLink(symbolsQuery.data)

  return (
    <div className="flex h-screen flex-col bg-panel text-ink">
      <TopBar />
      <div className="flex min-h-0 flex-1">
        <Watchlist livePrices={livePrices} />
        <div className="flex min-w-0 flex-1 flex-col">
          <ChartPanel
            ticker={ticker}
            liveCandle={liveCandle}
            livePrice={symbolId === null ? undefined : livePrices[symbolId]}
          />
          <section className="flex h-64 shrink-0 flex-col border-t border-border bg-panel-alt">
            <nav className="flex border-b border-border">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setBottomTab(tab.key)}
                  className={`px-3 py-1.5 text-2xs transition-colors ${
                    tab.key === bottomTab
                      ? 'border-b-2 border-accent text-ink'
                      : 'text-ink-dim hover:text-ink'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {bottomTab === 'news' && <NewsFeed ticker={ticker} />}
              {bottomTab === 'events' && <EventList />}
              {bottomTab === 'backtest' && <BacktestPanel ticker={ticker} />}
              {bottomTab === 'market' && <MarketSignals />}
              {bottomTab === 'sources' && <SourceHealth />}
            </div>
          </section>
        </div>
        <SignalPanel ticker={ticker} />
      </div>
    </div>
  )
}

function TopBar() {
  const { data: health } = useHealth()
  const wsConnected = useAppStore((s) => s.wsConnected)

  return (
    <header className="flex h-9 shrink-0 items-center gap-3 border-b border-border bg-panel-alt px-3">
      <span className="text-xs font-semibold tracking-wide">Investment Signal Platform</span>
      <span className="text-2xs text-ink-dim">Crypto · Vàng · Forex · CK Việt Nam</span>

      <div className="ml-auto flex items-center gap-3 text-2xs">
        <Dot ok={wsConnected} label={wsConnected ? 'realtime' : 'mất kết nối'} />
        {health && (
          <>
            <Dot ok={health.postgres} label="pg" />
            <Dot ok={health.clickhouse} label="ch" />
            <Dot ok={health.redis} label="redis" />
            <span className="text-ink-dim">engine {health.engine_version}</span>
          </>
        )}
      </div>
    </header>
  )
}

function Dot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1 text-ink-dim">
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-up' : 'bg-down'}`} />
      {label}
    </span>
  )
}
